import { z } from "zod";

/**
 * ============================================================
 * WORKSHOP CONTROLLED VOCABULARIES
 * ============================================================
 */

export const WORKSHOP_CATEGORIES = [
  "AI & Machine Learning",
];

export const WORKSHOP_MODES = ["Online", "Offline", "Hybrid"];

export const WORKSHOP_STATUSES = ["Draft", "Published", "Completed", "Cancelled"];

export const REGISTRATION_STATUSES = [
  "selected",
  "rejected",
  "attended",
];

export const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi (NCT)",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

/**
 * ============================================================
 * SUB-SCHEMAS FOR WORKSHOP DOMAIN
 * ============================================================
 */

export const InstructorSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Instructor name must be at least 2 characters"),
  role: z.string().min(2, "Instructor role is required"),
  bio: z.string().default(""),
  avatar_url: z.string().url("Invalid avatar URL").optional().or(z.literal("")),
  linkedin_url: z.string().url("Invalid LinkedIn URL").optional().or(z.literal("")),
});

export const AgendaItemSchema = z.object({
  topic: z.string().min(2, "Topic title is required"),
  description: z.string().optional().default(""),
});

export const GalleryItemSchema = z.object({
  url: z.string().url("Invalid gallery media URL"),
  caption: z.string().optional().default(""),
});

/**
 * ============================================================
 * FULL WORKSHOP SCHEMA (Domain Model)
 * ============================================================
 */

export const WorkshopSchema = z.object({
  // Identification & Routing
  id: z.string().min(1, "Workshop ID is required"),
  title: z.string().min(3, "Title must be at least 3 characters"),
  slug: z
    .string()
    .min(3, "Slug must be at least 3 characters")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must contain only lowercase letters, numbers, and hyphens"),
  short_description: z.string().min(10, "Short description must be at least 10 characters"),
  description: z.string().min(20, "Detailed description must be at least 20 characters"),

  // Categorization & Logistics
  category: z.enum(WORKSHOP_CATEGORIES, {
    errorMap: () => ({ message: "Please select a valid workshop category" }),
  }),
  mode: z.enum(WORKSHOP_MODES, {
    errorMap: () => ({ message: "Please select a valid delivery mode" }),
  }),
  venue: z.string().optional().default(""),
  meeting_link: z.string().optional().default(""),

  // Schedule
  date: z.string().min(1, "Workshop date is required"),
  start_time: z.string().min(1, "Start time is required"),
  end_time: z.string().min(1, "End time is required"),
  timezone: z.string().default("IST (UTC+5:30)"),
  duration: z.string().min(1, "Duration is required"),

  // Instructor Information
  instructor: InstructorSchema,
  instructors: z.array(InstructorSchema).optional().default([]),

  // Media
  cover_image: z.string().min(1, "Cover image is required"),
  gallery: z.array(GalleryItemSchema).default([]),

  // Curriculum & Requirements
  learning_outcomes: z.array(z.string()).min(1, "At least one learning outcome is required"),
  agenda: z.array(AgendaItemSchema).default([]),
  prerequisites: z.array(z.string()).default([]),
  requirements: z.array(z.string()).default([]),
  certificate_available: z.boolean().default(true),

  // Capacity & Pricing
  capacity: z.number().int().positive("Capacity must be greater than zero"),
  registration_count: z.number().int().nonnegative().default(0),
  is_free: z.boolean().default(true),
  price: z.number().nonnegative().optional().default(0),
  registration_deadline: z.string().optional().default(""),

  // Publishing & Lifecycle
  status: z.enum(WORKSHOP_STATUSES).default("Draft"),
  is_published: z.boolean().default(false),
  is_featured: z.boolean().default(false),
  show_in_promotional_modal: z.boolean().default(false),
  show_highlights: z.boolean().default(false),
  display_order: z.number().int().default(0),

  // Timestamps
  created_at: z.string().default(() => new Date().toISOString()),
  updated_at: z.string().default(() => new Date().toISOString()),
});

/**
 * ============================================================
 * WORKSHOP MEDIA MODEL (Supporting Photos & Video Highlights)
 * ============================================================
 */
export const WorkshopMediaItemSchema = z.object({
  id: z.string().optional(),
  workshop_id: z.string().optional(),
  storage_path: z.string().min(1, "Media storage URL is required"),
  media_type: z.enum(["cover", "gallery", "highlight", "other"]).default("gallery"),
  media_format: z.enum(["image", "video"]).default("image"),
  video_url: z.string().optional().default(""),
  thumbnail_url: z.string().optional().default(""),
  alt_text: z.string().optional().default(""),
  caption: z.string().optional().default(""),
  description: z.string().optional().default(""),
  display_order: z.number().int().default(0),
  is_published: z.boolean().default(true),
  is_featured: z.boolean().default(false),
});

/**
 * ============================================================
 * STUDENT REGISTRATION MODEL (Strictly 5 Student Fields)
 * ============================================================
 * Notice: Email, phone, address, LinkedIn, GitHub, password, etc.
 * are STRICTLY PROHIBITED in accordance with system design.
 */

export const StudentRegistrationFormSchema = z.object({
  // Field 1: Name
  name: z
    .string()
    .trim()
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name cannot exceed 100 characters"),

  // Field 2: Email
  email: z
    .string()
    .trim()
    .min(1, "Email address is required")
    .email("Please enter a valid email address")
    .max(100, "Email address cannot exceed 100 characters"),

  // Field 3: Resume (validated as File object on form, or URL string if pre-uploaded)
  resume: z
    .any()
    .refine((val) => val !== null && val !== undefined && val !== "", {
      message: "Please upload your resume (PDF or DOC/DOCX)",
    }),

  // Field 4: College
  college: z
    .string()
    .trim()
    .min(2, "College/University name must be at least 2 characters")
    .max(150, "College name cannot exceed 150 characters"),

  // Field 5: Residing City
  residing_city: z
    .string()
    .trim()
    .min(2, "City must be at least 2 characters")
    .max(80, "City cannot exceed 80 characters"),

  // Field 6: Residing State
  residing_state: z
    .string()
    .trim()
    .min(2, "State must be at least 2 characters")
    .max(80, "State cannot exceed 80 characters"),
});

/**
 * System Record Schema for saved workshop registrations.
 */
export const WorkshopRegistrationRecordSchema = z.object({
  id: z.string().min(1),
  workshop_id: z.string().min(1),
  workshop_title: z.string().default(""),

  // Applicant fields:
  name: z.string(),
  email: z.string().email(),
  resume_url: z.string().min(1, "Resume URL is required"),
  resume_filename: z.string().default("resume.pdf"),
  college: z.string(),
  residing_city: z.string(),
  residing_state: z.string(),

  // System status and audit
  status: z.enum(REGISTRATION_STATUSES).default("selected"),
  created_at: z.string().default(() => new Date().toISOString()),
});

/**
 * ============================================================
 * WORKSHOP TEMPORAL SCHEDULE & LIFECYCLE ENGINE
 * ============================================================
 * Derives temporal state dynamically from date, start_time, end_time,
 * and timezone without modifying the authoritative database status.
 */

/**
 * Parses human-readable time strings (e.g. "07:00 PM", "7:00 PM", "19:00")
 * into numeric hours (0-23) and minutes (0-59).
 */
export function parseTimeInto24Hour(timeStr) {
  if (!timeStr || typeof timeStr !== "string") {
    return { hours: 19, minutes: 0 };
  }
  const clean = timeStr.trim();
  const match = clean.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) {
    return { hours: 19, minutes: 0 };
  }
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const modifier = match[3] ? match[3].toUpperCase() : null;

  if (modifier === "PM" && hours < 12) {
    hours += 12;
  } else if (modifier === "AM" && hours === 12) {
    hours = 0;
  }
  return { hours, minutes };
}

/**
 * Extracts ISO-standard timezone offset string from workshop.timezone.
 * Defaults to "+05:30" (IST).
 */
export function parseTimezoneOffset(timezoneStr) {
  if (!timezoneStr || typeof timezoneStr !== "string") {
    return "+05:30";
  }
  if (timezoneStr.includes("IST") || timezoneStr.includes("+5:30") || timezoneStr.includes("+05:30")) {
    return "+05:30";
  }
  const match = timezoneStr.match(/([+-]\d{1,2}):?(\d{2})/);
  if (match) {
    const sign = match[1].startsWith("-") ? "-" : "+";
    const hrs = Math.abs(parseInt(match[1], 10)).toString().padStart(2, "0");
    const mins = match[2].padStart(2, "0");
    return `${sign}${hrs}:${mins}`;
  }
  return "+05:30";
}

/**
 * Reconstructs precise start and end Date objects for a workshop cohort.
 */
export function parseWorkshopSchedule(workshop) {
  if (!workshop || !workshop.date) {
    return { startDate: null, endDate: null };
  }

  const rawDate = String(workshop.date).trim();
  let datePart = "";

  // 1. Check if YYYY-MM-DD (or ISO string starting with YYYY-MM-DD)
  const ymdMatch = rawDate.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (ymdMatch) {
    const y = ymdMatch[1];
    const m = ymdMatch[2].padStart(2, "0");
    const d = ymdMatch[3].padStart(2, "0");
    datePart = `${y}-${m}-${d}`;
  } else {
    // 2. Check if DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = rawDate.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (dmyMatch) {
      const d = dmyMatch[1].padStart(2, "0");
      const m = dmyMatch[2].padStart(2, "0");
      const y = dmyMatch[3];
      datePart = `${y}-${m}-${d}`;
    } else {
      // 3. Verbal date like "20 September 2026" or "Sep 20, 2026"
      const parsedDate = new Date(rawDate);
      if (!isNaN(parsedDate.getTime())) {
        const y = parsedDate.getFullYear();
        const m = String(parsedDate.getMonth() + 1).padStart(2, "0");
        const d = String(parsedDate.getDate()).padStart(2, "0");
        datePart = `${y}-${m}-${d}`;
      }
    }
  }

  if (!datePart) {
    return { startDate: null, endDate: null };
  }

  const offset = parseTimezoneOffset(workshop.timezone);
  const start = parseTimeInto24Hour(workshop.start_time || "07:00 PM");
  const end = parseTimeInto24Hour(workshop.end_time || "08:30 PM");

  const pad = (n) => String(n).padStart(2, "0");

  const startIso = `${datePart}T${pad(start.hours)}:${pad(start.minutes)}:00${offset}`;
  const endIso = `${datePart}T${pad(end.hours)}:${pad(end.minutes)}:00${offset}`;

  let startDate = new Date(startIso);
  let endDate = new Date(endIso);

  if (isNaN(startDate.getTime())) {
    return { startDate: null, endDate: null };
  }

  if (isNaN(endDate.getTime())) {
    endDate = new Date(startDate.getTime() + 90 * 60 * 1000); // 1.5h default
  }

  // If end time is before start time (e.g. cross-midnight or misconfigured), add 2 hours to start
  if (endDate <= startDate) {
    endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  }

  return { startDate, endDate };
}

/**
 * Derives the temporal lifecycle state of a workshop based on its authoritative
 * database status and schedule.
 *
 * Possible return values:
 * - "draft": DB status is Draft
 * - "cancelled": DB status is Cancelled
 * - "completed": DB status is Completed
 * - "upcoming": DB status is Published, and now < start_time
 * - "live": DB status is Published, and start_time <= now <= end_time
 * - "completed_by_schedule": DB status is Published, but end_time has passed
 */
export function deriveWorkshopLifecycle(workshop, now = new Date()) {
  if (!workshop) return "draft";

  const dbStatus = workshop.status || "Draft";

  if (dbStatus === "Draft") return "draft";
  if (dbStatus === "Cancelled") return "cancelled";
  if (dbStatus === "Completed") return "completed";

  // When Published, derive lifecycle from schedule
  if (dbStatus === "Published" || workshop.is_published) {
    const { startDate, endDate } = parseWorkshopSchedule(workshop);
    // If schedule cannot be safely parsed, default strictly to "upcoming" (NEVER prematurely complete)
    if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
      return "upcoming";
    }

    const currentEpoch = now.getTime();
    if (currentEpoch < startDate.getTime()) {
      return "upcoming";
    }
    if (currentEpoch >= startDate.getTime() && currentEpoch <= endDate.getTime()) {
      return "live";
    }
    return "completed_by_schedule";
  }

  return "draft";
}

/**
 * ============================================================
 * DERIVED WORKSHOP UTILITY
 * ============================================================
 * Calculates dynamic client-side indicators without mutating
 * or storing redundant state in PostgreSQL or localStorage.
 */
export function calculateWorkshopDerived(workshop, now = new Date()) {
  if (!workshop) {
    return {
      spots_remaining: 0,
      is_registration_open: false,
      days_left: null,
      is_past: false,
      is_live: false,
      is_upcoming: false,
      is_completed: false,
      lifecycle_stage: "draft",
      show_highlights: false,
      show_in_promotional_modal: false,
      is_highlights_eligible: false,
      published_highlights_count: 0,
    };
  }

  const capacity = Number(workshop.capacity) || 0;
  const registrationCount = Number(workshop.registration_count) || 0;
  const spotsRemaining = Math.max(0, capacity - registrationCount);

  const { startDate, endDate } = parseWorkshopSchedule(workshop);
  const lifecycleStage = deriveWorkshopLifecycle(workshop, now);

  const isLive = lifecycleStage === "live";
  const isUpcoming = lifecycleStage === "upcoming";
  const isCompleted = lifecycleStage === "completed" || lifecycleStage === "completed_by_schedule";
  const isPast = isCompleted;

  const deadline = workshop.registration_deadline
    ? new Date(workshop.registration_deadline)
    : startDate;
  const isDeadlinePassed = deadline ? deadline < now : false;

  // Registration is ONLY open for published workshops that are currently upcoming
  // and have remaining capacity with deadline not passed
  const isRegistrationOpen =
    workshop.status === "Published" &&
    workshop.is_published === true &&
    lifecycleStage === "upcoming" &&
    spotsRemaining > 0 &&
    !isDeadlinePassed;

  let daysLeft = null;
  if (startDate && isUpcoming) {
    const diffTime = startDate.getTime() - now.getTime();
    daysLeft = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }

  const showHighlights = Boolean(workshop.show_highlights);
  const showInPromoModal = Boolean(workshop.show_in_promotional_modal);

  // Filter published highlights strictly (media_type === 'highlight' AND is_published !== false)
  const publishedHighlights = (Array.isArray(workshop.highlights) ? workshop.highlights : [])
    .filter((h) => h && h.media_type === "highlight" && h.is_published !== false);

  // Eligible to render highlights section strictly when:
  // 1. show_highlights is explicitly true (Admin master switch)
  // 2. Status is not Draft or Cancelled
  // 3. AT LEAST ONE published highlight media item exists
  const isHighlightsEligible =
    workshop.status !== "Draft" &&
    workshop.status !== "Cancelled" &&
    showHighlights &&
    publishedHighlights.length > 0;

  // Single deterministic public pipeline invariants:
  // - show_highlights=false => public_highlights = null
  // - show_highlights=true + 0 published highlights => public_highlights = null
  // - show_highlights=true + published highlights => publishedHighlights only
  const publicHighlights = isHighlightsEligible ? publishedHighlights : null;

  return {
    spots_remaining: spotsRemaining,
    is_registration_open: isRegistrationOpen,
    days_left: daysLeft,
    is_past: isPast,
    is_live: isLive,
    is_upcoming: isUpcoming,
    is_completed: isCompleted,
    lifecycle_stage: lifecycleStage,
    start_date_obj: startDate,
    end_date_obj: endDate,
    show_highlights: showHighlights,
    show_in_promotional_modal: showInPromoModal,
    is_highlights_eligible: isHighlightsEligible,
    published_highlights_count: isHighlightsEligible ? publishedHighlights.length : 0,
    public_highlights: publicHighlights,
  };
}

